/**
 * Metadata keys allowed on a strings.yml entry alongside locale values (en/sv/fi/…).
 * These must never be treated as locales when loading translations.
 *
 * `internal: true` marks bundled-only platform copy that must not be imported
 * into the API `/admin/strings` (see config/strings.yml header).
 */
export const TRANSLATION_META_KEYS = new Set(['internal']);

/**
 * Returns true when a raw YAML/JSON string entry is marked internal (not API-migratable).
 */
export function isInternalTranslationEntry(value: unknown): boolean {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value) && (value as Record<string, unknown>).internal === true);
}

/**
 * Drops metadata fields (e.g. `internal`) from a translations config map, leaving
 * only locale → string pairs for the runtime translation store.
 */
export function stripTranslationMeta(config: Record<string, unknown> = {}): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};

  for (const [key, value] of Object.entries(config)) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      continue;
    }

    const locales: Record<string, string> = {};
    for (const [locale, translation] of Object.entries(value as Record<string, unknown>)) {
      if (TRANSLATION_META_KEYS.has(locale)) {
        continue;
      }
      if (typeof translation !== 'string') {
        continue;
      }
      locales[locale] = translation;
    }

    out[key] = locales;
  }

  return out;
}
